import { Helmet } from 'react-helmet-async';

interface SEOProps {
    title: string;
    description: string;
    canonical?: string;
    ogImage?: string;
    ogType?: 'article' | 'website';
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
    keywords?: string;
    article?: boolean;
    noindex?: boolean;
}

const SEO = ({
    title,
    description,
    canonical,
    ogImage = 'https://aroan.com.br/og-image.png',
    ogType = 'article',
    author = 'Equipe Aroan',
    publishedTime,
    modifiedTime,
    keywords,
    article = true,
    noindex = false,
}: SEOProps) => {
    const fullTitle = `${title} | Aroan`;
    const siteUrl = 'https://aroan.com.br';
    const isTeamAuthor = /^equipe aroan$/i.test(author.trim());
    const fullCanonical = canonical || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : siteUrl);

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <meta name="author" content={author} />
            {!noindex && <link rel="canonical" href={fullCanonical} />}

            {/* Open Graph / Facebook / LinkedIn */}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={fullCanonical} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={title} />
            <meta property="og:site_name" content="Aroan" />
            <meta property="og:locale" content="pt_BR" />

            {/* Article specific tags */}
            {article && publishedTime && (
                <meta property="article:published_time" content={publishedTime} />
            )}
            {article && modifiedTime && (
                <meta property="article:modified_time" content={modifiedTime} />
            )}
            {article && author && (
                <meta property="article:author" content={author} />
            )}

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={fullCanonical} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content={title} />
            <meta name="twitter:site" content="@aroan" />
            <meta name="twitter:creator" content="@aroan" />

            {/* Additional SEO */}
            <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />

            {/* JSON-LD Structured Data for Article */}
            {article && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        "headline": title,
                        "description": description,
                        "image": ogImage,
                        "inLanguage": "pt-BR",
                        "author": isTeamAuthor
                            ? { "@id": "https://aroan.com.br/#organization" }
                            : { "@type": "Person", "name": author },
                        "publisher": { "@id": "https://aroan.com.br/#organization" },
                        "datePublished": publishedTime,
                        "dateModified": modifiedTime || publishedTime,
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": fullCanonical
                        }
                    })}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
